import React from 'react';
import { StepProps, TaxablePersonDetails } from '../../types';
import { Input } from '../ui/Input';
import { RadioGroup } from '../ui/RadioGroup';
import { Button } from '../ui/Button';

const SectionDivider: React.FC<{ title: string }> = ({ title }) => (
  <div className="mt-6 mb-3 border-b border-[#b98e4c] pb-2">
    <h3 className="text-lg font-medium text-[#1a2c46]">{title}</h3>
  </div>
);

const getTaxResidencyStatus = (details: TaxablePersonDetails): 'resident' | 'non-resident' | '' => {
    if (details.filingAs === 'natural_person' && details.naturalPersonSpecifics) {
        if(details.naturalPersonSpecifics.taxResidencyStatusDTA === 'non-resident') return 'non-resident';
        if(details.naturalPersonSpecifics.taxResidencyStatusDTA === 'resident') return 'resident'; // If DTA says resident, they are resident
        return details.naturalPersonSpecifics.uaeResidencyStatus === 'uae_resident' ? 'resident' : 'non-resident';
    }
    if ((details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') && details.juridicalPersonSpecifics) {
        const jp = details.juridicalPersonSpecifics;
        if (jp.isUaeOrFzIncorporated === 'yes') {
            return jp.isNonResidentInUaeUnderDTA === 'yes' ? 'non-resident' : 'resident';
        }
        if (jp.isForeignIncorporatedUaeControlled === 'yes') {
             return jp.isTaxResidentInForeignJurisdictionUnderDTA === 'yes' ? 'non-resident' : 'resident';
        }
        return 'non-resident'; 
    }
    return 'resident'; 
};


export const Step4AccountingAdjustments: React.FC<StepProps> = ({ formData, onChange, errors, t, openScheduleModal }) => {
  const data = formData.accountingAdjustments;
  const elections = formData.elections;
  const taxablePersonDetails = formData.taxablePersonDetails;
  const taxResidencyStatus = getTaxResidencyStatus(taxablePersonDetails);

  const radioOptionsYesNo: { label: string; value: string }[] = [{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-[#1a2c46] mb-6">{t('step4_title')}</h2>

      <p className="text-sm text-gray-500">{t('step4_accounting_income_base_note', { income: formData.accounting.accountingIncome || '0' })}</p>

      <SectionDivider title={t('s7_2_equity_method_title')} />
      <RadioGroup
        label={t('s7_2_equity_method_investment_accounting_label')}
        name="equityMethodInvestmentAccounting"
        value={data.equityMethodInvestmentAccounting}
        onChange={(val) => onChange('accountingAdjustments', 'equityMethodInvestmentAccounting', val)}
        options={radioOptionsYesNo}
        error={errors.equityMethodInvestmentAccounting as string}
        t={t}
        helpText={t('help_s4_equity_method')}
      />
      {data.equityMethodInvestmentAccounting === 'yes' && (
        <>
          <Input
            label={t('s7_2_share_of_profits_label')}
            type="number"
            id="equityMethodShareOfProfits"
            name="equityMethodShareOfProfits"
            value={data.equityMethodShareOfProfits}
            onChange={(e) => onChange('accountingAdjustments', 'equityMethodShareOfProfits', e.target.value)}
            error={errors.equityMethodShareOfProfits as string}
            placeholder={t('amount_aed_placeholder')}
          />
          <Input
            label={t('s7_2_share_of_losses_label')}
            type="number"
            id="equityMethodShareOfLosses"
            name="equityMethodShareOfLosses"
            value={data.equityMethodShareOfLosses}
            onChange={(e) => onChange('accountingAdjustments', 'equityMethodShareOfLosses', e.target.value)}
            error={errors.equityMethodShareOfLosses as string}
            placeholder={t('amount_aed_placeholder')}
          />
        </>
      )}

      <SectionDivider title={t('s7_3_unincorporated_partnership_title')} />
      <RadioGroup
        label={t('s7_3_unincorporated_partnership_separate_tp_label')}
        name="unincorporatedPartnershipSeparateTP"
        value={data.unincorporatedPartnershipSeparateTP}
        onChange={(val) => onChange('accountingAdjustments', 'unincorporatedPartnershipSeparateTP', val)}
        options={radioOptionsYesNo}
        error={errors.unincorporatedPartnershipSeparateTP as string}
        t={t}
      />
      {data.unincorporatedPartnershipSeparateTP === 'yes' && (
        <>
          <Input
            label={t('s7_3_unincorporated_partnership_net_profits_label')}
            type="number"
            id="unincorporatedPartnershipNetProfits"
            value={data.unincorporatedPartnershipNetProfits}
            onChange={(e) => onChange('accountingAdjustments', 'unincorporatedPartnershipNetProfits', e.target.value)}
            error={errors.unincorporatedPartnershipNetProfits as string}
            placeholder={t('amount_aed_placeholder')}
          />
          <Input
            label={t('s7_3_unincorporated_partnership_net_losses_label')}
            type="number"
            id="unincorporatedPartnershipNetLosses"
            value={data.unincorporatedPartnershipNetLosses}
            onChange={(e) => onChange('accountingAdjustments', 'unincorporatedPartnershipNetLosses', e.target.value)}
            error={errors.unincorporatedPartnershipNetLosses as string}
            placeholder={t('amount_aed_placeholder')}
          />
          <Input
            label={t('s7_3_up_interest_disposal_gains_pe_label')}
            type="number"
            id="upInterestDisposalGainsPE"
            value={data.upInterestDisposalGainsPE || ''}
            onChange={(e) => onChange('accountingAdjustments', 'upInterestDisposalGainsPE', e.target.value)}
            error={errors.upInterestDisposalGainsPE as string}
            placeholder={t('amount_aed_placeholder')}
          />
          <Input
            label={t('s7_3_up_interest_disposal_losses_pe_label')}
            type="number"
            id="upInterestDisposalLossesPE"
            value={data.upInterestDisposalLossesPE || ''}
            onChange={(e) => onChange('accountingAdjustments', 'upInterestDisposalLossesPE', e.target.value)}
            error={errors.upInterestDisposalLossesPE as string}
            placeholder={t('amount_aed_placeholder')}
          />
        </>
      )}

      <SectionDivider title={t('s7_4_income_losses_not_in_is_title')} />
      <RadioGroup
        label={t('s7_4_income_losses_not_in_is_recognised_label')}
        name="incomeLossesNotInISRecognised"
        value={data.incomeLossesNotInISRecognised}
        onChange={(val) => onChange('accountingAdjustments', 'incomeLossesNotInISRecognised', val)}
        options={radioOptionsYesNo}
        error={errors.incomeLossesNotInISRecognised as string}
        t={t}
        helpText={t('s7_4_sch22_1_link_note')}
      />
      {data.incomeLossesNotInISRecognised === 'yes' && openScheduleModal && (
        <>
           <Button
            type="button"
            onClick={() => openScheduleModal('schedule22Other', formData.schedule22Other)}
            variant="secondary"
            className="mb-2 text-sm"
          >
            {t('open_schedule_button', {schedule_name: t('sch22_1_title')})}
          </Button>
          <Input
            label={t('s7_4_gains_not_in_is_label')}
            type="text"
            id="gainsNotInIS"
            value={data.gainsNotInIS}
            readOnly
            className="bg-gray-100"
            error={errors.gainsNotInIS as string}
            placeholder={t('amount_aed_placeholder')}
          />
          <Input
            label={t('s7_4_losses_not_in_is_label')}
            type="text"
            id="lossesNotInIS"
            value={data.lossesNotInIS}
            readOnly
            className="bg-gray-100"
            error={errors.lossesNotInIS as string}
            placeholder={t('amount_aed_placeholder')}
          />
        </>
      )}

      {elections.realisationBasis && (
        <fieldset className="p-4 border rounded-md bg-slate-50">
          <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s7_5_realisation_basis_title')}</legend>
          <p className="text-xs text-gray-500 mb-2">{t('s7_5_realisation_basis_note')}</p>
          {openScheduleModal && (
            <Button
              type="button"
              onClick={() => openScheduleModal('schedule22Other', formData.schedule22Other)}
              variant="secondary"
              className="mb-3 text-sm"
            >
              {t('open_schedule_button', {schedule_name: `${t('sch22_2_title')} / ${t('sch22_3_title')}`})}
            </Button>
          )}
          <Input
            label={t('s7_5_unrealised_gains_current_label')}
            type="text"
            id="unrealisedGainsRecognisedCurrentTaxPeriod"
            value={data.unrealisedGainsRecognisedCurrentTaxPeriod}
            readOnly
            className="bg-gray-100"
            error={errors.unrealisedGainsRecognisedCurrentTaxPeriod as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_5_sch22_2_link_note')}
          />
           <Input
            label={t('s7_5_unrealised_losses_current_label')}
            type="text"
            id="unrealisedLossesRecognisedCurrentTaxPeriod"
            value={data.unrealisedLossesRecognisedCurrentTaxPeriod}
            readOnly
            className="bg-gray-100"
            error={errors.unrealisedLossesRecognisedCurrentTaxPeriod as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_5_sch22_2_link_note')}
          />
           <Input
            label={t('s7_5_deferred_gains_realised_label')}
            type="text"
            id="deferredGainsRealisedCurrentTaxPeriod"
            value={data.deferredGainsRealisedCurrentTaxPeriod || ''}
            readOnly
            className="bg-gray-100"
            error={errors.deferredGainsRealisedCurrentTaxPeriod as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_5_sch22_3_link_note')}
          />
           <Input
            label={t('s7_5_deferred_losses_realised_label')}
            type="text"
            id="deferredLossesRealisedCurrentTaxPeriod"
            value={data.deferredLossesRealisedCurrentTaxPeriod || ''}
            readOnly
            className="bg-gray-100"
            error={errors.deferredLossesRealisedCurrentTaxPeriod as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_5_sch22_3_link_note')}
          />
           <Input label={t('s7_5_depreciation_decrease_ti_label')} type="number" id="depreciationAdjustmentsRealisationDecrease" value={data.depreciationAdjustmentsRealisationDecrease || ''} onChange={e => onChange('accountingAdjustments', 'depreciationAdjustmentsRealisationDecrease', e.target.value)} error={errors.depreciationAdjustmentsRealisationDecrease as string} placeholder={t('amount_aed_placeholder')} />
           <Input label={t('s7_5_depreciation_increase_ti_label')} type="number" id="depreciationAdjustmentsRealisationIncrease" value={data.depreciationAdjustmentsRealisationIncrease || ''} onChange={e => onChange('accountingAdjustments', 'depreciationAdjustmentsRealisationIncrease', e.target.value)} error={errors.depreciationAdjustmentsRealisationIncrease as string} placeholder={t('amount_aed_placeholder')} />
           <Input label={t('s7_5_other_realisation_adjustments_decrease_ti_label')} type="number" id="otherRealisationAdjustmentsDecreaseTI" value={data.otherRealisationAdjustmentsDecreaseTI || ''} onChange={e => onChange('accountingAdjustments', 'otherRealisationAdjustmentsDecreaseTI', e.target.value)} error={errors.otherRealisationAdjustmentsDecreaseTI as string} placeholder={t('amount_aed_placeholder')} />
           <Input label={t('s7_5_other_realisation_adjustments_increase_ti_label')} type="number" id="otherRealisationAdjustmentsIncreaseTI" value={data.otherRealisationAdjustmentsIncreaseTI || ''} onChange={e => onChange('accountingAdjustments', 'otherRealisationAdjustmentsIncreaseTI', e.target.value)} error={errors.otherRealisationAdjustmentsIncreaseTI as string} placeholder={t('amount_aed_placeholder')} />
           <Input label={t('s7_5_other_adjustments_desc_label')} type="text" id="otherRealisationAdjustmentsDesc" value={data.otherRealisationAdjustmentsDesc || ''} onChange={e => onChange('accountingAdjustments', 'otherRealisationAdjustmentsDesc', e.target.value)} error={errors.otherRealisationAdjustmentsDesc as string} placeholder={t('s7_5_other_adjustments_desc_placeholder')} />
        </fieldset>
      )}
       {(elections.transitionalRulesImmovableProperty || elections.transitionalRulesIntangibleAssets || elections.transitionalRulesFinancialAssetsLiabilities) && openScheduleModal && (
        <fieldset className="p-4 border rounded-md bg-slate-50">
          <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s7_6_transitional_rules_title')}</legend>
          <Button
            type="button"
            onClick={() => openScheduleModal('schedule21TransitionalRules', formData.schedule21TransitionalRules)}
            variant="secondary"
            className="mb-3 text-sm"
          >
            {t('open_schedule_button', {schedule_name: t('sch21_title')})}
          </Button>
          {elections.transitionalRulesImmovableProperty && (
           <Input
            label={t('s7_6_gains_on_elected_immovable_property_label')}
            type="text"
            id="gainsOnElectedImmovableProperty"
            value={data.gainsOnElectedImmovableProperty}
            readOnly
            className="bg-gray-100"
            error={errors.gainsOnElectedImmovableProperty as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_6_sch21_link_note')}
          />
          )}
          {elections.transitionalRulesIntangibleAssets && (
             <Input
                label={t('s7_6_adjustment_intangible_assets_label')}
                type="text"
                id="transitionalRulesIntangibleAssetsAdjustment"
                value={data.transitionalRulesIntangibleAssetsAdjustment}
                readOnly
                className="bg-gray-100"
                error={errors.transitionalRulesIntangibleAssetsAdjustment as string}
                placeholder={t('amount_aed_placeholder')}
                helpText={t('s7_6_sch21_link_note')}
             />
          )}
          {elections.transitionalRulesFinancialAssetsLiabilities && (
             <Input
                label={t('s7_6_adjustment_financial_assets_liabilities_label')}
                type="text"
                id="transitionalRulesFinancialAssetsLiabilitiesAdjustment"
                value={data.transitionalRulesFinancialAssetsLiabilitiesAdjustment}
                readOnly
                className="bg-gray-100"
                error={errors.transitionalRulesFinancialAssetsLiabilitiesAdjustment as string}
                placeholder={t('amount_aed_placeholder')}
                helpText={t('s7_6_sch21_link_note')}
             />
          )}
        </fieldset>
      )}

      <SectionDivider title={t('s7_7_exempt_income_title')} />
      <RadioGroup
        label={t('s7_7_dividends_from_uae_resident_label')}
        name="dividendsFromUaeResident"
        value={data.dividendsFromUaeResident}
        onChange={(val) => onChange('accountingAdjustments', 'dividendsFromUaeResident', val)}
        options={radioOptionsYesNo}
        error={errors.dividendsFromUaeResident as string}
        t={t}
      />
      {data.dividendsFromUaeResident === 'yes' && openScheduleModal && (
        <>
          <Button
            type="button"
            onClick={() => openScheduleModal('schedule13UaeDividends', formData.schedule13UaeDividends)}
            variant="secondary"
            className="mb-2 text-sm"
          >
            {t('open_schedule_button', {schedule_name: t('sch13_title')})}
          </Button>
          <Input
            label={t('s7_7_total_dividends_label')}
            type="text"
            id="totalDividendsFromUaeResident"
            value={data.totalDividendsFromUaeResident}
            readOnly
            className="bg-gray-100"
            error={errors.totalDividendsFromUaeResident as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_7_sch13_link_note')}
          />
        </>
      )}

      <RadioGroup
        label={t('s7_7_income_losses_from_participating_interest_label')}
        name="incomeLossesFromParticipatingInterest"
        value={data.incomeLossesFromParticipatingInterest}
        onChange={(val) => onChange('accountingAdjustments', 'incomeLossesFromParticipatingInterest', val)}
        options={radioOptionsYesNo}
        error={errors.incomeLossesFromParticipatingInterest as string}
        t={t}
      />
      {data.incomeLossesFromParticipatingInterest === 'yes' && openScheduleModal && (
        <>
          <Button
            type="button"
            onClick={() => openScheduleModal('schedule18ParticipationExemption', formData.schedule18ParticipationExemption)}
            variant="secondary"
            className="mb-2 text-sm"
          >
            {t('open_schedule_button', {schedule_name: t('sch18_title')})}
          </Button>
          <Input
            label={t('s7_7_income_from_participating_interest_exempted_label')}
            type="text"
            id="incomeFromParticipatingInterestExempted"
            value={data.incomeFromParticipatingInterestExempted}
            readOnly
            className="bg-gray-100"
            error={errors.incomeFromParticipatingInterestExempted as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_7_sch18_link_note')}
          />
           <Input label={t('s7_7_income_clawback_label')} type="number" id="incomeFromParticipatingInterestClawback" value={data.incomeFromParticipatingInterestClawback || ''} onChange={e => onChange('accountingAdjustments', 'incomeFromParticipatingInterestClawback', e.target.value)} error={errors.incomeFromParticipatingInterestClawback as string} placeholder={t('amount_aed_placeholder')} />
           <Input label={t('s7_7_losses_clawback_label')} type="number" id="lossesFromParticipatingInterestClawback" value={data.lossesFromParticipatingInterestClawback || ''} onChange={e => onChange('accountingAdjustments', 'lossesFromParticipatingInterestClawback', e.target.value)} error={errors.lossesFromParticipatingInterestClawback as string} placeholder={t('amount_aed_placeholder')} />
        </>
      )}

      {elections.hasForeignPEs && elections.foreignPermanentEstablishmentExemption && (
        <fieldset className="p-4 border rounded-md bg-slate-50">
          <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s7_8_foreign_pe_exemption_title')}</legend>
          <p className="text-xs text-gray-500 mb-2">{t('s7_8_foreign_pe_exemption_help')}</p>
          {openScheduleModal && (
            <Button
                type="button"
                onClick={() => openScheduleModal('schedule14ForeignPE', formData.schedule14ForeignPE)}
                variant="secondary"
                className="mb-2 text-sm"
            >
                {t('open_schedule_button', {schedule_name: t('sch14_title')})}
            </Button>
          )}
          <Input
            label={t('s7_8_taxable_income_loss_from_eligible_foreign_pe_label')}
            type="text"
            id="taxableIncomeLossFromEligibleForeignPE"
            value={data.taxableIncomeLossFromEligibleForeignPE}
            readOnly
            className="bg-gray-100"
            error={errors.taxableIncomeLossFromEligibleForeignPE as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s7_8_sch14_link_note')}
          />
        </fieldset>
      )}

      {taxResidencyStatus === 'non-resident' && (
         <fieldset className="p-4 border rounded-md bg-slate-50">
            <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s7_9_intl_aircraft_shipping_title')}</legend>
            <RadioGroup
                label={t('s7_9_income_derived_label')}
                name="incomeFromInternationalAircraftShippingNonResident"
                value={data.incomeFromInternationalAircraftShippingNonResident}
                onChange={(val) => {
                onChange('accountingAdjustments', 'incomeFromInternationalAircraftShippingNonResident', val);
                if (val === 'no') { // Clear related fields if 'No' is selected
                    onChange('accountingAdjustments', 'countryInternationalAircraftShipping', '');
                    onChange('accountingAdjustments', 'incomeDerivedIntlAircraftShipping', '');
                    onChange('accountingAdjustments', 'expenditureIntlAircraftShipping', '');
                }
                }}
                options={radioOptionsYesNo}
                error={errors.incomeFromInternationalAircraftShippingNonResident as string}
                t={t}
            />
            {data.incomeFromInternationalAircraftShippingNonResident === 'yes' && (
                <>
                <Input label={t('s7_9_country_label')} type="text" id="countryInternationalAircraftShipping" value={data.countryInternationalAircraftShipping} onChange={e => onChange('accountingAdjustments', 'countryInternationalAircraftShipping', e.target.value)} error={errors.countryInternationalAircraftShipping as string} />
                <Input label={t('s7_9_income_amount_label')} type="number" id="incomeDerivedIntlAircraftShipping" value={data.incomeDerivedIntlAircraftShipping} onChange={e => onChange('accountingAdjustments', 'incomeDerivedIntlAircraftShipping', e.target.value)} error={errors.incomeDerivedIntlAircraftShipping as string} placeholder={t('amount_aed_placeholder')} />
                <Input label={t('s7_9_expenditure_label')} type="number" id="expenditureIntlAircraftShipping" value={data.expenditureIntlAircraftShipping} onChange={e => onChange('accountingAdjustments', 'expenditureIntlAircraftShipping', e.target.value)} error={errors.expenditureIntlAircraftShipping as string} placeholder={t('amount_aed_placeholder')} />
                </>
            )}
         </fieldset>
      )}
      <p className="mt-4 text-sm text-gray-600">{t('step4_note')}</p>
      {formData.elections.smallBusinessRelief && (
         <p className="mt-2 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md text-sm">
           {t('sbr_skip_note')}
         </p>
      )}
    </div>
  );
};